// @flow
// Новое лекало: тот же редактор, что для правки, но лист пустой. Шапка файла
// (поле, скорость, лист) и угол, куда встаёт лекало, берутся у соседнего
// лекала той же модели: новое режется так же, как остальные.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import gql from 'graphql-tag';
import { sanitizeRouteParams } from 'metaup/routing/routingUtils';
import type { Routes } from 'metaup/routing/routingUtils';
import { apolloQuery } from '../core/data/apolloClient';
import Loader from '../elements/Loader';
import { decodeException } from '../elements/LoadFailed';
import {
  DEFAULT_CORNER,
  contourSegments,
  coordsOf,
  emptyDoc,
  pairIndex,
  parsePrn,
  plotterTransform,
  segmentsBox,
  templateOf,
} from './prn/prnModel';
import VariantEditorView from './views/VariantEditorView';
import { createVariantFromEditor, fetchPrnText } from './VariantEditorPage';

// Угол соседнего лекала берём, если он у самого края листа, как у всех
// лекал из Корела (обычно X 0, Y 0,2 мм); иначе — обычный угол
const NEAR_CORNER = 50;

async function loadCreate(deviceId) {
  const device = await apolloQuery(gql`
    query ($id: String!) {
      getDevice(id: $id) {
        id
        name
        brand { id name }
        variants(filter: { _sort: "name" }) {
          id
          name
          cut { id downloadUrl }
          preview {
            id
            fit(width: 300, height: 300) { width height }
          }
        }
      }
    }
  `, { id: deviceId });

  // Образец — первое лекало модели на обычном листе, которое открывается
  let sample = null;
  const withFiles = device.variants.filter(v => v.cut && v.cut.downloadUrl).slice(0, 5);
  for (let n = 0; n < withFiles.length && !sample; n += 1) {
    try {
      const doc = parsePrn(await fetchPrnText(withFiles[n].cut.downloadUrl));
      const { xy, refs } = coordsOf(doc);
      const template = templateOf(doc);
      if (template && !plotterTransform(xy).shifted) {
        const pairs = pairIndex(refs);
        const box = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
        doc.contours.forEach((_, i) => segmentsBox(contourSegments(doc, i, xy, pairs), box));
        const near = v => v >= 0 && v <= NEAR_CORNER;
        const corner = near(box.minX) && near(box.minY)
          ? { x: Math.round(box.minX), y: Math.round(box.minY) }
          : DEFAULT_CORNER;
        sample = { variant: withFiles[n], template, corner };
      }
    } catch (e) {
      // битый или недоступный файл — смотрим следующее лекало
    }
  }

  // Превью в каталоге стоит вертикально; как у образца, если он есть
  const withPreview = (sample && sample.variant.preview) ? sample.variant : device.variants.find(v => v.preview);
  const fit = withPreview && withPreview.preview.fit;
  const previewPortrait = fit ? fit.height > fit.width : true;

  return {
    device,
    doc: sample ? emptyDoc(sample.template.header, sample.template.tail) : emptyDoc(),
    corner: sample ? sample.corner : DEFAULT_CORNER,
    sampleName: sample ? sample.variant.name : null,
    previewPortrait,
  };
}

type Props = {
  deviceId: string,
};

function VariantCreatePage({ deviceId }: Props) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    setError(null);
    loadCreate(deviceId)
      .then(d => { if (alive) setData(d); })
      .catch(e => { if (alive) setError(decodeException(e)); });
    return () => { alive = false; };
  }, [deviceId]);

  if (error) {
    return (
      <div className="alert alert-danger">
        <p className="mb-2">Не открыть редактор для нового лекала. {error}</p>
        <Link to="/library/brands/">К брендам</Link>
      </div>
    );
  }
  if (!data) return <Loader />;

  const { device } = data;
  const variant = {
    id: null,
    name: 'Новое лекало',
    deviceId: device.id,
    guide: null,
    preview: null,
    device: {
      id: device.id,
      name: device.name,
      brand: device.brand,
      variants: device.variants.map(v => ({ id: v.id, name: v.name })),
    },
  };

  return (
    <VariantEditorView
      key={deviceId}
      mode="create"
      variant={variant}
      doc={data.doc}
      refs={[]}
      initialXy={[]}
      shifted={false}
      corner={data.corner}
      previewPortrait={data.previewPortrait}
      onSave={async params => {
        const created = await createVariantFromEditor({
          ...params,
          deviceId: device.id,
          guide: null,
          notes: `Сделано в редакторе, ${new Date().toLocaleDateString('ru-RU')}`,
        });
        // Новое название занято: второй раз под ним не сохранить
        setData(d => ({
          ...d,
          device: { ...d.device, variants: d.device.variants.concat([{ ...created, cut: null, preview: null }]) },
        }));
        return created;
      }}
    />
  );
}

export function variantCreatePageRoutes(): Routes {
  return [
    {
      title: 'Новое лекало',
      path: '/variant-create/:deviceId/',
      isEnabled: ({ isUser }) => isUser,
      render: params => (
        <VariantCreatePage
          {...sanitizeRouteParams(params, {
            deviceId: 'id',
          })}
        />
      ),
      design: null, // eslint-disable-line global-require
    },
  ];
}
